import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { PedidoService } from '../../core/services/pedido.service';
import { AlertService } from '../../core/services/alert.service';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';
import { PedidoListApi } from '../../core/models/api/pedido-api';

@Component({
    selector: 'app-admin-entregas',
    standalone: true,
    imports: [CommonModule, FormsModule, UiLoadingComponent],
    template: `
    <div class="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 class="text-2xl font-black text-gray-800">Entregas</h2>
        <p class="text-sm text-gray-400 mt-1">Marca los pedidos listos que salen a ruta y cierra cada entrega como Entregado o No entregado.</p>
      </div>
      <label class="block min-w-60">
        <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Buscar</span>
        <input [ngModel]="key()" (ngModelChange)="buscar($event)" placeholder="Nro, correo, teléfono, doc…" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200 bg-white" />
      </label>
      <button type="button" (click)="pasarAEnCamino()" [disabled]="seleccionados().length === 0 || enviando()" class="px-5 py-3 rounded-2xl text-white text-sm font-bold disabled:opacity-40 cursor-pointer" style="background-color: var(--bg-main);">
        {{ enviando() ? 'Enviando…' : 'Pasar a En camino (' + seleccionados().length + ')' }}
      </button>
    </div>

    <app-ui-loading *ngIf="loading()"></app-ui-loading>

    <div *ngIf="!loading()" class="bg-white rounded-[2rem] border border-rose-100 shadow-sm overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
            <th class="px-5 py-3"></th>
            <th class="px-5 py-3">Nro</th>
            <th class="px-5 py-3">Estado</th>
            <th class="px-5 py-3">Total</th>
            <th class="px-5 py-3">Entrega</th>
            <th class="px-5 py-3"></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let p of pedidos()" class="border-b border-gray-50">
            <td class="px-5 py-3">
              <input *ngIf="p.estadoCodigo === 'LISTO'" type="checkbox" [checked]="estaSeleccionado(p.uuid)" (change)="alternar(p.uuid)" class="w-5 h-5 accent-rose-500 cursor-pointer" />
            </td>
            <td class="px-5 py-3">
              <p class="font-black text-gray-800">#{{ p.numeroPedido }}</p>
              <p class="text-[11px] text-gray-400">{{ p.correoContacto }}</p>
            </td>
            <td class="px-5 py-3"><span class="text-[10px] font-bold uppercase px-2 py-1 rounded-full" [class.bg-amber-50]="p.estadoCodigo === 'LISTO'" [class.text-amber-600]="p.estadoCodigo === 'LISTO'" [class.bg-sky-50]="p.estadoCodigo === 'EN_CAMINO'" [class.text-sky-600]="p.estadoCodigo === 'EN_CAMINO'">{{ p.estado }}</span></td>
            <td class="px-5 py-3 font-bold text-rose-500">S/ {{ p.total.toFixed(2) }}</td>
            <td class="px-5 py-3 text-gray-600">
              <p>{{ p.fechaEntrega }} · {{ p.rangoHorario }}</p>
              <p *ngIf="p.tipoEntrega" class="text-[11px] text-gray-400">{{ p.tipoEntrega }}<span *ngIf="p.direccion"> · {{ p.direccion }}</span><span *ngIf="p.referencia"> ({{ p.referencia }})</span></p>
            </td>
            <td class="px-5 py-3 text-right whitespace-nowrap">
              <ng-container *ngIf="p.estadoCodigo === 'EN_CAMINO'">
                <div *ngIf="cerrando() === p.uuid" class="flex flex-wrap items-center justify-end gap-2">
                  <input [(ngModel)]="motivo" placeholder="Motivo (ej: no estaba, dirección errada)" class="w-64 rounded-xl border border-gray-200 px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-rose-200" />
                  <button type="button" (click)="cerrar(p, 'NO_ENTREGADO')" [disabled]="!motivo().trim()" class="px-3 py-1.5 rounded-xl bg-red-500 text-white text-xs font-bold disabled:opacity-40 cursor-pointer">Confirmar</button>
                  <button type="button" (click)="cerrando.set(null)" class="px-3 py-1.5 rounded-xl border border-gray-200 text-gray-500 text-xs font-bold cursor-pointer">Cancelar</button>
                </div>
                <ng-container *ngIf="cerrando() !== p.uuid">
                  <button type="button" (click)="cerrar(p, 'ENTREGADO')" class="px-3 py-1.5 rounded-xl bg-emerald-500 text-white text-xs font-bold cursor-pointer mr-1">Entregado</button>
                  <button type="button" (click)="cerrando.set(p.uuid)" class="px-3 py-1.5 rounded-xl border border-red-200 text-red-500 text-xs font-bold cursor-pointer">No entregado</button>
                </ng-container>
              </ng-container>
            </td>
          </tr>
        </tbody>
      </table>
      <p *ngIf="pedidos().length === 0" class="px-5 py-8 text-center text-sm text-gray-400">No hay entregas pendientes.</p>
    </div>
    `
})
export class AdminEntregasComponent implements OnInit {
    private pedidosService = inject(PedidoService);
    private alerts = inject(AlertService);

    loading = signal(true);
    enviando = signal(false);
    pedidos = signal<PedidoListApi[]>([]);
    seleccion = signal<Record<string, boolean>>({});
    cerrando = signal<string | null>(null);
    motivo = signal('');
    key = signal('');
    private searchTimer: ReturnType<typeof setTimeout> | null = null;

    ngOnInit(): void {
        this.cargar();
    }

    buscar(term: string) {
        this.key.set(term);
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        this.searchTimer = setTimeout(() => this.cargar(), 400);
    }

    seleccionados(): string[] {
        const sel = this.seleccion();
        return Object.keys(sel).filter((k) => sel[k]);
    }

    estaSeleccionado(uuid: string): boolean {
        return !!this.seleccion()[uuid];
    }

    alternar(uuid: string) {
        this.seleccion.update((sel) => ({ ...sel, [uuid]: !sel[uuid] }));
    }

    pasarAEnCamino() {
        const ids = this.seleccionados();
        if (!ids.length || this.enviando()) {
            return;
        }
        this.enviando.set(true);
        forkJoin(ids.map((uuid) => this.pedidosService.cambiarEstado(uuid, 'EN_CAMINO', null))).subscribe({
            next: () => {
                this.enviando.set(false);
                this.seleccion.set({});
                this.alerts.ok(`${ids.length} pedido(s) en camino`);
                this.cargar();
            },
            error: (err) => {
                this.enviando.set(false);
                this.alerts.error(err?.message || 'No se pudo actualizar.');
                this.cargar();
            }
        });
    }

    cerrar(p: PedidoListApi, codigo: 'ENTREGADO' | 'NO_ENTREGADO') {
        if (codigo === 'NO_ENTREGADO' && !this.motivo().trim()) {
            return;
        }
        const comentario = codigo === 'NO_ENTREGADO' ? this.motivo().trim() : null;
        this.pedidosService.cambiarEstado(p.uuid, codigo, comentario).subscribe({
            next: () => {
                this.alerts.ok(`Pedido #${p.numeroPedido} ${codigo === 'ENTREGADO' ? 'entregado' : 'devuelto a taller'}`);
                this.cerrando.set(null);
                this.motivo.set('');
                this.cargar();
            },
            error: (err) => this.alerts.error(err?.message || 'No se pudo actualizar.')
        });
    }

    private cargar() {
        this.loading.set(true);
        forkJoin([
            this.pedidosService.listPedidosStaff('LISTO', this.key() || undefined, 1, 50),
            this.pedidosService.listPedidosStaff('EN_CAMINO', this.key() || undefined, 1, 50)
        ]).subscribe({
            next: ([a, b]) => {
                this.pedidos.set([...a.data, ...b.data].sort((x, y) => y.createdAt.localeCompare(x.createdAt)));
                this.loading.set(false);
            },
            error: (err) => {
                this.loading.set(false);
                this.alerts.error(err?.message || 'No se pudieron cargar los pedidos.');
            }
        });
    }
}
